import { DECIMAL_INPUT_PATTERN } from '@/scopes/common/components/FormElements/utils/decimalPatternUtils'
import { ACCOMMODATION_SPECIAL_REQUIREMENT_VALUES } from '../accommodationSpecialRequirements'
import { LuggageTypeEntry, SpecialLuggageBaseValues } from '../engine/types'

export interface MedicalEquipmentValues extends SpecialLuggageBaseValues {
  medicalEquipmentType?: string
  isBringingMedicine: string
  isNeedingExtraMedicineKilos: string
  isFoldedWheelchair: string
  foldedWidth: string
  foldedHeight: string
  foldedLength: string
  canWalk: string
  canClimbStairs: string
  canClimbAircraftStairs: string
  wheelchairNeeds: string
  isSeatReservationMade: string
  isOnlineCheckInCompleted: string
  otherMedicalBagType: string
  transportationArea: string
  isCpapHandLuggageCompliant: string
  isMedicalEquipmentNeededOnBoard: string
  isMedicalAirportAssistanceNeeded: string
  width: string
  height: string
  length: string
  weight: string
  brandOfBattery: string
  voltageOfBattery: string
  ampereHoursOfBattery: string
  wattHoursOfBattery: string
  typeOfBattery: string
  weightOfBattery: string
  canBatteryBeDisconnected: string
  isWheelchairTiltPossible: string
  isDryOrWetBattery: string
  accommodationSpecialRequirements: string[]
  otherAccommodationSpecialRequirements: string
  termsAndConditions: boolean
  medicalCertificate: boolean
}

const MEDICAL_EQUIPMENT_TYPE_VALUES = {
  BAG: 'bag',
  MANUAL_WHEELCHAIR: 'manual-wheelchair',
  ELECTRIC_WHEELCHAIR: 'electric-wheelchair',
  CPAP: 'cpap',
  OXYGEN: 'oxygen',
  OTHER_MEDICAL_DEVICE: 'other-medical-equipment',
  OTHER: 'other',
}

const TRANSPORTATION_AREA_VALUES = {
  CABIN: 'cabin',
  HOLD_LUGGAGE: 'hold-luggage',
}

const YES_NO_OPTIONS = [
  { labelKey: 'booking_details_special_luggage_medical_equipment_option_yes', value: 'yes' },
  { labelKey: 'booking_details_special_luggage_medical_equipment_option_no', value: 'no' },
]

interface MedicalEquipmentEntryLinks {
  oxygenBanner?: string
  termsAndConditions?: string
  medicalCertificate?: string
  medicineInfo?: string
}

export function createMedicalEquipmentEntry({ links }: { links?: MedicalEquipmentEntryLinks } = {}): LuggageTypeEntry<MedicalEquipmentValues> {
  const { oxygenBanner = '', termsAndConditions = '', medicalCertificate = '', medicineInfo = '' } = links ?? {}

  const isBagSelected = (values: MedicalEquipmentValues) =>
    values.medicalEquipmentType === MEDICAL_EQUIPMENT_TYPE_VALUES.BAG

  const isWheelchairSelected = (values: MedicalEquipmentValues) =>
    values.medicalEquipmentType === MEDICAL_EQUIPMENT_TYPE_VALUES.MANUAL_WHEELCHAIR
    || values.medicalEquipmentType === MEDICAL_EQUIPMENT_TYPE_VALUES.ELECTRIC_WHEELCHAIR

  const isElectricWheelchairSelected = (values: MedicalEquipmentValues) =>
    values.medicalEquipmentType === MEDICAL_EQUIPMENT_TYPE_VALUES.ELECTRIC_WHEELCHAIR

  const isOtherMedicalDeviceSelected = (values: MedicalEquipmentValues) =>
    values.medicalEquipmentType === MEDICAL_EQUIPMENT_TYPE_VALUES.OTHER_MEDICAL_DEVICE

  const isCpapSelected = (values: MedicalEquipmentValues) =>
    values.medicalEquipmentType === MEDICAL_EQUIPMENT_TYPE_VALUES.CPAP

  const isOxygenSelected = (values: MedicalEquipmentValues) =>
    values.medicalEquipmentType === MEDICAL_EQUIPMENT_TYPE_VALUES.OXYGEN

  const isFoldedWheelchairEnabled = (values: MedicalEquipmentValues) =>
    isWheelchairSelected(values) && values.isFoldedWheelchair === 'yes'

  const isCpapHoldLuggage = (values: MedicalEquipmentValues) =>
    isCpapSelected(values) && values.transportationArea === TRANSPORTATION_AREA_VALUES.HOLD_LUGGAGE

  const isCpapCabin = (values: MedicalEquipmentValues) =>
    isCpapSelected(values) && values.transportationArea === TRANSPORTATION_AREA_VALUES.CABIN

  const isCpapCabinNotCompliant = (values: MedicalEquipmentValues) =>
    isCpapCabin(values) && values.isCpapHandLuggageCompliant === 'no'

  const showDimensions = (values: MedicalEquipmentValues) =>
    (isBagSelected(values) && values.isNeedingExtraMedicineKilos === 'yes')
    || isWheelchairSelected(values)
    || isOtherMedicalDeviceSelected(values)
    || isCpapHoldLuggage(values)
    || isCpapCabinNotCompliant(values)

  const showBatteryDetails = (values: MedicalEquipmentValues) =>
    isElectricWheelchairSelected(values)
    || isOtherMedicalDeviceSelected(values)
    || isCpapHoldLuggage(values)
    || isCpapCabinNotCompliant(values)

  const isBatteryFieldRequired = (values: MedicalEquipmentValues) =>
    isElectricWheelchairSelected(values) || isCpapHoldLuggage(values)

  return {
    schema: {
      defaultValues: {
        termsAndConditions: false,
        medicalCertificate: false,
        medicalEquipmentType: '',
        isBringingMedicine: '',
        isNeedingExtraMedicineKilos: '',
        isFoldedWheelchair: '',
        foldedWidth: '',
        foldedHeight: '',
        foldedLength: '',
        canWalk: '',
        canClimbStairs: '',
        canClimbAircraftStairs: '',
        wheelchairNeeds: '',
        isSeatReservationMade: '',
        isOnlineCheckInCompleted: '',
        otherMedicalBagType: '',
        transportationArea: '',
        isCpapHandLuggageCompliant: '',
        isMedicalEquipmentNeededOnBoard: '',
        isMedicalAirportAssistanceNeeded: '',
        width: '',
        height: '',
        length: '',
        weight: '',
        brandOfBattery: '',
        voltageOfBattery: '',
        ampereHoursOfBattery: '',
        wattHoursOfBattery: '',
        typeOfBattery: '',
        weightOfBattery: '',
        canBatteryBeDisconnected: '',
        isWheelchairTiltPossible: '',
        isDryOrWetBattery: '',
        accommodationSpecialRequirements: [],
        otherAccommodationSpecialRequirements: '',
      },
      sections: [
        {
          id: 'medicalInstructionText',
          fields: [
            {
              kind: 'paragraph',
              messageKey: 'booking_details_special_luggage_medical_equipment_medical_instruction_text',
            },
          ],
        },
        {
          id: 'medicalEquipmentBasic',
          fields: [
            {
              kind: 'dropdown',
              name: 'medicalEquipmentType',
              labelKey: 'booking_details_special_luggage_medical_equipment_type_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_medical_equipment_type_required_error',
              options: [
                {
                  labelKey: `booking_details_special_luggage_medical_equipment_type_option_bag`,
                  value: MEDICAL_EQUIPMENT_TYPE_VALUES.BAG,
                },
                {
                  labelKey: `booking_details_special_luggage_medical_equipment_type_option_manual-wheelchair`,
                  value: MEDICAL_EQUIPMENT_TYPE_VALUES.MANUAL_WHEELCHAIR,
                },
                {
                  labelKey: `booking_details_special_luggage_medical_equipment_type_option_electric-wheelchair`,
                  value: MEDICAL_EQUIPMENT_TYPE_VALUES.ELECTRIC_WHEELCHAIR,
                },
                {
                  labelKey: `booking_details_special_luggage_medical_equipment_type_option_cpap`,
                  value: MEDICAL_EQUIPMENT_TYPE_VALUES.CPAP,
                },
                {
                  labelKey: `booking_details_special_luggage_medical_equipment_type_option_oxygen`,
                  value: MEDICAL_EQUIPMENT_TYPE_VALUES.OXYGEN,
                },
                {
                  labelKey: `booking_details_special_luggage_medical_equipment_type_option_other-medical-device`,
                  value: MEDICAL_EQUIPMENT_TYPE_VALUES.OTHER_MEDICAL_DEVICE,
                },
                {
                  labelKey: `booking_details_special_luggage_medical_equipment_type_option_other`,
                  value: MEDICAL_EQUIPMENT_TYPE_VALUES.OTHER,
                },
              ],
              placeholderKey: 'booking_details_special_luggage_medical_equipment_type_placeholder',
            },
          ],
        },
        {
          id: 'isBringingMedicine',
          fields: [
            {
              kind: 'choiceList',
              name: 'isBringingMedicine',
              labelKey: 'booking_details_special_luggage_medical_equipment_is_bringing_medicine_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_medical_equipment_is_bringing_medicine_required_error',
              options: YES_NO_OPTIONS,
            },
          ],
          visibleWhen: isBagSelected,
        },
        {
          id: 'bringingMedicineInfoBanner',
          fields: [
            {
              kind: 'banner',
              messageKey: 'booking_details_special_luggage_medical_equipment_bringing_medicine_banner_message',
              links: {
                medicineInfoLink: {
                  label: medicineInfo,
                  href: medicineInfo,
                },
              },
            },
          ],
          visibleWhen: values => isBagSelected(values) && values.isBringingMedicine === 'yes',
        },
        {
          id: 'isNeedingExtraMedicineKilos',
          fields: [
            {
              kind: 'choiceList',
              name: 'isNeedingExtraMedicineKilos',
              labelKey: 'booking_details_special_luggage_medical_equipment_is_needing_extra_medicine_kilos_label',
              required: true,
              requiredErrorKey:
                'booking_details_special_luggage_medical_equipment_is_needing_extra_medicine_kilos_required_error',
              options: YES_NO_OPTIONS,
            },
          ],
          visibleWhen: isBagSelected,
        },
        {
          id: 'isFoldedWheelchair',
          fields: [
            {
              kind: 'choiceList',
              name: 'isFoldedWheelchair',
              labelKey: 'booking_details_special_luggage_manual_wheelchair_is_folded_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_manual_wheelchair_is_folded_required_error',
              options: YES_NO_OPTIONS,
            },
          ],
          visibleWhen: isWheelchairSelected,
        },
        {
          id: 'otherMedicalEquipmentType',
          layout: 'row',
          fields: [
            {
              kind: 'text',
              name: 'otherMedicalBagType',
              labelKey: 'booking_details_special_luggage_medical_equipment_other_medical_bag_type_label',
              required: true,
              requiredErrorKey:
                'booking_details_special_luggage_medical_equipment_other_medical_bag_type_required_error',
            },
          ],
          visibleWhen: values =>
            isBagSelected(values)
            || values.medicalEquipmentType === MEDICAL_EQUIPMENT_TYPE_VALUES.OTHER
            || isOtherMedicalDeviceSelected(values),
        },
        {
          id: 'transportationAreaDropdown',
          fields: [
            {
              kind: 'dropdown',
              name: 'transportationArea',
              labelKey: 'booking_details_special_luggage_medical_equipment_transportation_area_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_medical_equipment_transportation_area_required_error',
              options: [
                {
                  labelKey: 'booking_details_special_luggage_medical_equipment_transportation_area_option_cabin',
                  value: TRANSPORTATION_AREA_VALUES.CABIN,
                },
                {
                  labelKey: 'booking_details_special_luggage_medical_equipment_transportation_area_option_hold-luggage',
                  value: TRANSPORTATION_AREA_VALUES.HOLD_LUGGAGE,
                },
              ],
              placeholderKey: 'booking_details_special_luggage_medical_equipment_transportation_area_placeholder',
            },
          ],
          visibleWhen: values => isOtherMedicalDeviceSelected(values) || isCpapSelected(values),
        },
        {
          id: 'isCpapHandLuggageCompliant',
          fields: [
            {
              kind: 'choiceList',
              name: 'isCpapHandLuggageCompliant',
              labelKey: 'booking_details_special_luggage_medical_equipment_cpap_hand_luggage_compliant_label',
              required: true,
              requiredErrorKey:
                'booking_details_special_luggage_medical_equipment_cpap_hand_luggage_compliant_required_error',
              options: YES_NO_OPTIONS,
            },
          ],
          visibleWhen: isCpapCabin,
        },
        {
          id: 'isMedicalEquipmentNeededOnBoard',
          fields: [
            {
              kind: 'choiceList',
              name: 'isMedicalEquipmentNeededOnBoard',
              labelKey: 'booking_details_special_luggage_medical_equipment_needed_on_board_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_medical_equipment_needed_on_board_required_error',
              options: YES_NO_OPTIONS,
            },
          ],
          visibleWhen: values =>
            (isOtherMedicalDeviceSelected(values) || isCpapSelected(values))
            && values.transportationArea === TRANSPORTATION_AREA_VALUES.CABIN,
        },
        {
          id: 'oxygenInfoBanner',
          fields: [
            {
              kind: 'banner',
              messageKey: 'booking_details_special_luggage_medical_equipment_oxygen_banner_message',
              links: {
                oxygenInfoLink: {
                  labelKey: 'booking_details_special_luggage_medical_equipment_oxygen_banner_link_label',
                  href: oxygenBanner,
                },
              },
            },
          ],
          visibleWhen: isOxygenSelected,
        },
        {
          id: 'isMedicalAirportAssistanceNeeded',
          fields: [
            {
              kind: 'choiceList',
              name: 'isMedicalAirportAssistanceNeeded',
              labelKey: 'booking_details_special_luggage_medical_airport_assistance_needed_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_medical_airport_assistance_needed_required_error',
              options: YES_NO_OPTIONS,
            },
          ],
          visibleWhen: values =>
            isOtherMedicalDeviceSelected(values)
            || isOxygenSelected(values)
            || isCpapCabin(values)
            || isCpapHoldLuggage(values),
        },
        {
          id: 'measureHelpText',
          fields: [
            {
              kind: 'paragraph',
              messageKey: 'booking_details_special_luggage_measure_help_text',
            },
          ],
          visibleWhen: showDimensions,
        },
        {
          id: 'specialLuggageDimensionsRow1',
          layout: 'row',
          conditionalTitles: [
            {
              titleKey: 'booking_details_special_luggage_manual_wheelchair_unfolded_dimensions_title',
              visibleWhen: isWheelchairSelected,
            },
            {
              titleKey: 'booking_details_special_luggage_medical_bag_measurements_title',
              visibleWhen: values => isBagSelected(values) && values.isNeedingExtraMedicineKilos === 'yes',
            },
          ],
          fields: [
            {
              kind: 'text',
              name: 'width',
              labelKey: 'booking_details_special_luggage_width_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_dimensions_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
            {
              kind: 'text',
              name: 'height',
              labelKey: 'booking_details_special_luggage_height_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_dimensions_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
          ],
          visibleWhen: showDimensions,
        },
        {
          id: 'specialLuggageDimensionsRow2',
          layout: 'row',
          fields: [
            {
              kind: 'text',
              name: 'length',
              labelKey: 'booking_details_special_luggage_length_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_dimensions_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
            {
              kind: 'text',
              name: 'weight',
              labelKey: 'booking_details_special_luggage_weight_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_dimensions_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
          ],
          visibleWhen: showDimensions,
        },
        {
          id: 'manualWheelchairFoldedDimensionsRow1',
          layout: 'row',
          titleKey: 'booking_details_special_luggage_manual_wheelchair_folded_dimensions_title',
          fields: [
            {
              kind: 'text',
              name: 'foldedWidth',
              labelKey: 'booking_details_special_luggage_manual_wheelchair_folded_width_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_manual_wheelchair_folded_width_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
            {
              kind: 'text',
              name: 'foldedHeight',
              labelKey: 'booking_details_special_luggage_manual_wheelchair_folded_height_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_manual_wheelchair_folded_height_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
          ],
          visibleWhen: isFoldedWheelchairEnabled,
        },
        {
          id: 'manualWheelchairFoldedDimensionsRow2',
          layout: 'row',
          fields: [
            {
              kind: 'text',
              name: 'foldedLength',
              labelKey: 'booking_details_special_luggage_manual_wheelchair_folded_length_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_manual_wheelchair_folded_length_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
          ],
          visibleWhen: isFoldedWheelchairEnabled,
        },
        {
          id: 'manualWheelchairMobility',
          fields: [
            {
              kind: 'choiceList',
              name: 'canWalk',
              labelKey: 'booking_details_special_luggage_manual_wheelchair_can_walk_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_manual_wheelchair_can_walk_required_error',
              options: YES_NO_OPTIONS,
            },
            {
              kind: 'choiceList',
              name: 'canClimbStairs',
              labelKey: 'booking_details_special_luggage_manual_wheelchair_can_climb_stairs_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_manual_wheelchair_can_climb_stairs_required_error',
              options: YES_NO_OPTIONS,
            },
            {
              kind: 'choiceList',
              name: 'canClimbAircraftStairs',
              labelKey: 'booking_details_special_luggage_manual_wheelchair_can_climb_aircraft_stairs_label',
              required: true,
              requiredErrorKey:
                'booking_details_special_luggage_manual_wheelchair_can_climb_aircraft_stairs_required_error',
              options: YES_NO_OPTIONS,
            },
          ],
          visibleWhen: isWheelchairSelected,
        },
        {
          id: 'manualWheelchairNeeds',
          fields: [
            {
              kind: 'choiceList',
              name: 'wheelchairNeeds',
              labelKey: 'booking_details_special_luggage_manual_wheelchair_needs_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_manual_wheelchair_needs_required_error',
              options: [
                {
                  labelKey: 'booking_details_special_luggage_manual_wheelchair_needs_option_check_in_desk',
                  value: 'check-in',
                },
                {
                  labelKey: 'booking_details_special_luggage_manual_wheelchair_needs_option_gate',
                  value: 'gate',
                },
              ],
            },
          ],
          visibleWhen: isWheelchairSelected,
        },
        {
          id: 'manualWheelchairTravelPreparation',
          fields: [
            {
              kind: 'choiceList',
              name: 'isSeatReservationMade',
              labelKey: 'booking_details_special_luggage_manual_wheelchair_is_seat_reservation_made_label',
              required: true,
              requiredErrorKey:
                'booking_details_special_luggage_manual_wheelchair_is_seat_reservation_made_required_error',
              options: YES_NO_OPTIONS,
            },
            {
              kind: 'choiceList',
              name: 'isOnlineCheckInCompleted',
              labelKey: 'booking_details_special_luggage_manual_wheelchair_is_online_check_in_completed_label',
              required: true,
              requiredErrorKey:
                'booking_details_special_luggage_manual_wheelchair_is_online_check_in_completed_required_error',
              options: YES_NO_OPTIONS,
            },
          ],
          visibleWhen: isWheelchairSelected,
        },
        {
          id: 'batteryDetailsRow1',
          layout: 'row',
          fields: [
            {
              kind: 'text',
              name: 'brandOfBattery',
              labelKey: 'booking_details_special_luggage_medical_equipment_brand_of_battery_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_medical_equipment_brand_of_battery_required_error',
            },
            {
              kind: 'text',
              name: 'voltageOfBattery',
              labelKey: 'booking_details_special_luggage_medical_equipment_voltage_of_battery_label',
              required: isBatteryFieldRequired,
              requiredErrorKey: 'booking_details_special_luggage_medical_equipment_voltage_of_battery_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
          ],
          visibleWhen: showBatteryDetails,
        },
        {
          id: 'batteryDetailsRow2',
          layout: 'row',
          fields: [
            {
              kind: 'text',
              name: 'ampereHoursOfBattery',
              labelKey: 'booking_details_special_luggage_medical_equipment_ampere_hours_of_battery_label',
              required: isBatteryFieldRequired,
              requiredErrorKey:
                'booking_details_special_luggage_medical_equipment_ampere_hours_of_battery_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
            {
              kind: 'text',
              name: 'wattHoursOfBattery',
              labelKey: 'booking_details_special_luggage_medical_equipment_watt_hours_of_battery_label',
              required: isBatteryFieldRequired,
              requiredErrorKey:
                'booking_details_special_luggage_medical_equipment_watt_hours_of_battery_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
          ],
          visibleWhen: showBatteryDetails,
        },
        {
          id: 'batteryDetailsRow3',
          layout: 'row',
          fields: [
            {
              kind: 'text',
              name: 'typeOfBattery',
              labelKey: 'booking_details_special_luggage_medical_equipment_type_of_battery_label',
              helpTextKey: 'booking_details_special_luggage_medical_equipment_type_of_battery_help_text',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_medical_equipment_type_of_battery_required_error',
            },
            {
              kind: 'text',
              name: 'weightOfBattery',
              labelKey: 'booking_details_special_luggage_medical_equipment_weight_of_battery_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_medical_equipment_weight_of_battery_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
          ],
          visibleWhen: isElectricWheelchairSelected,
        },
        {
          id: 'canBatteryBeDisconnected',
          fields: [
            {
              kind: 'choiceList',
              name: 'canBatteryBeDisconnected',
              labelKey: 'booking_details_special_luggage_medical_equipment_can_battery_be_disconnected_label',
              required: true,
              requiredErrorKey:
                'booking_details_special_luggage_medical_equipment_can_battery_be_disconnected_required_error',
              options: YES_NO_OPTIONS,
            },
          ],
          visibleWhen: isElectricWheelchairSelected,
        },
        {
          id: 'isWheelchairTiltPossible',
          fields: [
            {
              kind: 'choiceList',
              name: 'isWheelchairTiltPossible',
              labelKey: 'booking_details_special_luggage_medical_equipment_is_wheelchair_tilt_possible_label',
              required: true,
              requiredErrorKey:
                'booking_details_special_luggage_medical_equipment_is_wheelchair_tilt_possible_required_error',
              options: YES_NO_OPTIONS,
            },
          ],
          visibleWhen: isElectricWheelchairSelected,
        },
        {
          id: 'isDryOrWetBattery',
          fields: [
            {
              kind: 'choiceList',
              name: 'isDryOrWetBattery',
              labelKey: 'booking_details_special_luggage_medical_equipment_is_dry_or_wet_battery_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_medical_equipment_is_dry_or_wet_battery_required_error',
              options: [
                {
                  labelKey: 'booking_details_special_luggage_medical_equipment_is_dry_or_wet_battery_option_dry',
                  value: 'dry',
                },
                {
                  labelKey: 'booking_details_special_luggage_medical_equipment_is_dry_or_wet_battery_option_wet',
                  value: 'wet',
                },
              ],
            },
          ],
          visibleWhen: isElectricWheelchairSelected,
        },
        {
          id: 'accommodationSpecialRequirements',
          layout: 'row',
          fields: [
            {
              kind: 'checkboxGroup',
              name: 'accommodationSpecialRequirements', // accommodation-special-requirements-dropdown-choice-list
              labelKey: 'booking_details_special_luggage_medical_equipment_acco_special_requirements_label',
              options: [
                {
                  value: ACCOMMODATION_SPECIAL_REQUIREMENT_VALUES.ELEVATOR,
                  labelKey:
                    'booking_details_special_luggage_medical_equipment_acco_special_requirements_elevator_label',
                },
                {
                  value: ACCOMMODATION_SPECIAL_REQUIREMENT_VALUES.GROUND,
                  labelKey: 'booking_details_special_luggage_medical_equipment_acco_special_requirements_ground_label',
                },
                {
                  value: ACCOMMODATION_SPECIAL_REQUIREMENT_VALUES.ACCESSIBLE,
                  labelKey:
                    'booking_details_special_luggage_medical_equipment_acco_special_requirements_accessible_label',
                },
                {
                  value: ACCOMMODATION_SPECIAL_REQUIREMENT_VALUES.SHOWER,
                  labelKey: 'booking_details_special_luggage_medical_equipment_acco_special_requirements_shower_label',
                },
                {
                  value: ACCOMMODATION_SPECIAL_REQUIREMENT_VALUES.OTHER,
                  labelKey: 'booking_details_special_luggage_medical_equipment_acco_special_requirements_other_label',
                },
              ],
            },
          ],
          visibleWhen: values =>
            isWheelchairSelected(values)
            || isOtherMedicalDeviceSelected(values)
            || isOxygenSelected(values)
            || isCpapSelected(values),
        },
        {
          id: 'otherAccommodationSpecialRequirements',
          fields: [
            {
              kind: 'text',
              name: 'otherAccommodationSpecialRequirements',
              labelKey:
                'booking_details_special_luggage_medical_equipment_other_acco_special_requirements_label',
              required: true,
              requiredErrorKey:
                'booking_details_special_luggage_medical_equipment_other_acco_special_requirements_required_error',
            },
          ],
          visibleWhen: values =>
            values.accommodationSpecialRequirements?.includes(ACCOMMODATION_SPECIAL_REQUIREMENT_VALUES.OTHER) ?? false,
        },
        {
          id: 'termsAndConditions',
          fields: [
            {
              kind: 'checkbox',
              name: 'termsAndConditions',
              labelKey: 'booking_details_special_luggage_terms_and_conditions_label',
              validationMessageKey: 'booking_details_special_luggage_terms_and_conditions_required_error',
              links: {
                termsAndConditionsLink: {
                  labelKey: 'booking_details_special_luggage_terms_and_conditions_link_label',
                  href: termsAndConditions,
                },
              },
            },
          ],
          visibleWhen: values =>
            isBagSelected(values)
            || isWheelchairSelected(values)
            || isOtherMedicalDeviceSelected(values)
            || isOxygenSelected(values)
            || isCpapSelected(values),
        },
        {
          id: 'medicalCertificate',
          fields: [
            {
              kind: 'checkbox',
              name: 'medicalCertificate',
              labelKey: 'booking_details_special_luggage_medical_certificate_label',
              validationMessageKey: 'booking_details_special_luggage_medical_certificate_required_error',
              links: {
                medicalCertificateLink: {
                  labelKey: 'booking_details_special_luggage_medical_certificate_link_label',
                  href: medicalCertificate,
                },
              },
            },
          ],
          visibleWhen: values =>
            isBagSelected(values)
            || isOtherMedicalDeviceSelected(values)
            || isOxygenSelected(values)
            || isCpapSelected(values),
        },
      ],
    },
  }
}
